<!-- src/app/pages/dashboard/projected-impact/projected-impact.component.html -->
<div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
    <!-- Cascade of Care -->
    <div class="bg-surface-0 rounded-2xl shadow-lg px-4 py-4 lg:px-6 lg:py-5">
        <div class="flex flex-col md:flex-row justify-between gap-3 mb-3">
            <div class="flex flex-col gap-1">
                <h2 class="text-sm mb-0 font-semibold text-slate-900">Cascade of Care</h2>
                <p class="text-[11px] text-slate-500">
                    @if (selectedCascadeSimulation === 'Basic Simulation') {
                    Basic Simulation (Year - {{ projectedYear }})
                    } @else {
                    Basic Simulation vs {{ selectedCascadeSimulation }} (Year - {{ projectedYear }})
                    }
                </p>
            </div>
        </div>
        <div>
            <p-chart type="bar" [data]="cascadeData" [options]="cascadeOptions"
                style="height: 100%; width: 100%"></p-chart>
        </div>
    </div>

    <!-- Impact / CVD card -->
    <div class="bg-surface-0 rounded-2xl shadow-lg overflow-hidden h-full">
        <div class="px-4 py-4 lg:px-6 lg:py-5 border-b border-surface-200">
            <div class="flex items-center justify-between">
                <div>
                    <h2 class="text-sm font-semibold text-slate-900 mb-0">Projected impact</h2>
                    <p class="text-[11px] text-slate-500 mt-1">
                        @if (selectedInfoSimulation === 'Basic Simulation') {
                        Comparison of Basic Simulation between {{ baselineYear }} and {{ projectedYear }}
                        } @else {
                        Comparison of Basic Simulation vs {{ selectedInfoSimulation }} at year {{ projectedYear }} ({{ selectedInfoPeriod }} horizon)
                        }
                    </p>
                </div>

                <div class="inline-flex overflow-hidden rounded-md border border-surface-200 bg-surface-50">
                    <button type="button" class="px-3 py-1.5 text-xs font-semibold transition-colors"
                        [ngClass]="htCvdView === 'ht' ? 'bg-[#d41c58] text-white' : 'bg-transparent text-slate-500 hover:text-slate-700'"
                        (click)="htCvdView = 'ht'">
                        HT
                    </button>
                    <button type="button" class="px-3 py-1.5 text-xs font-semibold transition-colors"
                        [ngClass]="htCvdView === 'cvd' ? 'bg-[#d41c58] text-white' : 'bg-transparent text-slate-500 hover:text-slate-700'"
                        (click)="htCvdView = 'cvd'">
                        CVD
                    </button>
                </div>
            </div>
        </div>

        <!-- HT list view -->
        @if (htCvdView === 'ht') {
        <div class="divide-y divide-surface-200">
            <!-- Undiagnosed -->
            <div class="px-4 py-3 lg:px-6 flex items-center gap-3">
                <div class="h-10 w-10 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center">
                    <i class="pi pi-search text-sm"></i>
                </div>
                <div class="flex-1">
                    <div class="text-sm font-semibold text-slate-900">Undiagnosed</div>
                    <div class="text-[11px] text-slate-500 mt-0.5">
                        <span class="font-semibold text-slate-700">{{ format(htImpactCounts.undiagnosed.baseline)
                            }}</span>
                        <span class="mx-1 text-slate-400">→</span>
                        <span class="font-semibold text-slate-700">{{ format(htImpactCounts.undiagnosed.new) }}</span>
                    </div>
                </div>
                <div class="text-right">
                    <div class="inline-flex items-center gap-1 text-xs font-semibold"
                        [class.text-emerald-600]="htImpactCounts.undiagnosed.deltaPct > 0"
                        [class.text-rose-600]="htImpactCounts.undiagnosed.deltaPct < 0"
                        [class.text-slate-500]="htImpactCounts.undiagnosed.deltaPct === 0">
                        <i class="pi" [class.pi-sort-up-fill]="htImpactCounts.undiagnosed.deltaPct > 0"
                            [class.pi-sort-down-fill]="htImpactCounts.undiagnosed.deltaPct < 0"
                            [class.pi-minus]="htImpactCounts.undiagnosed.deltaPct === 0"></i>
                        <span>{{ htImpactCounts.undiagnosed.deltaPct }}%</span>
                    </div>
                    <div class="text-[11px] font-semibold"
                        [class.text-emerald-600]="htImpactCounts.undiagnosed.deltaAbs > 0"
                        [class.text-rose-600]="htImpactCounts.undiagnosed.deltaAbs < 0"
                        [class.text-slate-500]="htImpactCounts.undiagnosed.deltaAbs === 0">
                        {{ format(htImpactCounts.undiagnosed.deltaAbs) }}
                    </div>
                </div>
            </div>
            <!-- Diagnosed -->
            <div class="px-4 py-3 lg:px-6 flex items-center gap-3">
                <div class="h-10 w-10 rounded-full bg-sky-50 text-sky-600 flex items-center justify-center">
                    <i class="pi pi-verified text-sm"></i>
                </div>
                <div class="flex-1">
                    <div class="text-sm font-semibold text-slate-900">Diagnosed</div>
                    <div class="text-[11px] text-slate-500 mt-0.5">
                        <span class="font-semibold text-slate-700">{{ format(htImpactCounts.diagnosed.baseline)
                            }}</span>
                        <span class="mx-1 text-slate-400">→</span>
                        <span class="font-semibold text-slate-700">{{ format(htImpactCounts.diagnosed.new) }}</span>
                    </div>
                </div>
                <div class="text-right">
                    <div class="inline-flex items-center gap-1 text-xs font-semibold"
                        [class.text-emerald-600]="htImpactCounts.diagnosed.deltaPct > 0"
                        [class.text-rose-600]="htImpactCounts.diagnosed.deltaPct < 0"
                        [class.text-slate-500]="htImpactCounts.diagnosed.deltaPct === 0">
                        <i class="pi" [class.pi-sort-up-fill]="htImpactCounts.diagnosed.deltaPct > 0"
                            [class.pi-sort-down-fill]="htImpactCounts.diagnosed.deltaPct < 0"
                            [class.pi-minus]="htImpactCounts.diagnosed.deltaPct === 0"></i>
                        <span>{{ htImpactCounts.diagnosed.deltaPct }}%</span>
                    </div>
                    <div class="text-[11px] font-semibold"
                        [class.text-emerald-600]="htImpactCounts.diagnosed.deltaAbs > 0"
                        [class.text-rose-600]="htImpactCounts.diagnosed.deltaAbs < 0"
                        [class.text-slate-500]="htImpactCounts.diagnosed.deltaAbs === 0">
                        {{ format(htImpactCounts.diagnosed.deltaAbs) }}
                    </div>
                </div>
            </div>
            <!-- Treated -->
            <div class="px-4 py-3 lg:px-6 flex items-center gap-3">
                <div class="h-10 w-10 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center">
                    <i class="pi pi-heart text-sm"></i>
                </div>
                <div class="flex-1">
                    <div class="text-sm font-semibold text-slate-900">Treated</div>
                    <div class="text-[11px] text-slate-500 mt-0.5">
                        <span class="font-semibold text-slate-700">{{ format(htImpactCounts.treated.baseline) }}</span>
                        <span class="mx-1 text-slate-400">→</span>
                        <span class="font-semibold text-slate-700">{{ format(htImpactCounts.treated.new) }}</span>
                    </div>
                </div>
                <div class="text-right">
                    <div class="inline-flex items-center gap-1 text-xs font-semibold"
                        [class.text-emerald-600]="htImpactCounts.treated.deltaPct > 0"
                        [class.text-rose-600]="htImpactCounts.treated.deltaPct < 0"
                        [class.text-slate-500]="htImpactCounts.treated.deltaPct === 0">
                        <i class="pi" [class.pi-sort-up-fill]="htImpactCounts.treated.deltaPct > 0"
                            [class.pi-sort-down-fill]="htImpactCounts.treated.deltaPct < 0"
                            [class.pi-minus]="htImpactCounts.treated.deltaPct === 0"></i>
                        <span>{{ htImpactCounts.treated.deltaPct }}%</span>
                    </div>
                    <div class="text-[11px] font-semibold"
                        [class.text-emerald-600]="htImpactCounts.treated.deltaAbs > 0"
                        [class.text-rose-600]="htImpactCounts.treated.deltaAbs < 0"
                        [class.text-slate-500]="htImpactCounts.treated.deltaAbs === 0">
                        {{ format(htImpactCounts.treated.deltaAbs) }}
                    </div>
                </div>
            </div>
            <!-- Controlled -->
            <div class="px-4 py-3 lg:px-6 flex items-center gap-3">
                <div class="h-10 w-10 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center">
                    <i class="pi pi-check-circle text-sm"></i>
                </div>
                <div class="flex-1">
                    <div class="text-sm font-semibold text-slate-900">Controlled</div>
                    <div class="text-[11px] text-slate-500 mt-0.5">
                        <span class="font-semibold text-slate-700">{{ format(htImpactCounts.controlled.baseline)
                            }}</span>
                        <span class="mx-1 text-slate-400">→</span>
                        <span class="font-semibold text-slate-700">{{ format(htImpactCounts.controlled.new) }}</span>
                    </div>
                </div>
                <div class="text-right">
                    <div class="inline-flex items-center gap-1 text-xs font-semibold"
                        [class.text-emerald-600]="htImpactCounts.controlled.deltaPct > 0"
                        [class.text-rose-600]="htImpactCounts.controlled.deltaPct < 0"
                        [class.text-slate-500]="htImpactCounts.controlled.deltaPct === 0">
                        <i class="pi" [class.pi-sort-up-fill]="htImpactCounts.controlled.deltaPct > 0"
                            [class.pi-sort-down-fill]="htImpactCounts.controlled.deltaPct < 0"
                            [class.pi-minus]="htImpactCounts.controlled.deltaPct === 0"></i>
                        <span>{{ htImpactCounts.controlled.deltaPct }}%</span>
                    </div>
                    <div class="text-[11px] font-semibold"
                        [class.text-emerald-600]="htImpactCounts.controlled.deltaAbs > 0"
                        [class.text-rose-600]="htImpactCounts.controlled.deltaAbs < 0"
                        [class.text-slate-500]="htImpactCounts.controlled.deltaAbs === 0">
                        {{ format(htImpactCounts.controlled.deltaAbs) }}
                    </div>
                </div>
            </div>
        </div>
        <div class="px-4 py-3 lg:px-6 bg-surface-50 text-[11px] text-slate-500">
            <span class="font-semibold text-slate-700">Note:</span>
            {{ htNote }}
        </div>
        } @else {
        <div class="divide-y divide-surface-200">
            @for (item of cvdSummary; track item) {
            <div class="px-4 py-3 lg:px-6 flex items-center gap-3">
                <div class="h-10 w-10 rounded-full bg-slate-50 text-slate-600 flex items-center justify-center">
                    <i [ngClass]="item.iconClass" class="text-sm"></i>
                </div>
                <div class="flex-1">
                    <div class="text-sm font-semibold text-slate-900">{{ item.title }}</div>
                    <div class="text-[11px] text-slate-500 mt-0.5">
                        <span class="font-semibold text-slate-700">{{ format(item.baseline) }}</span>
                        <span class="mx-1 text-slate-400">→</span>
                        <span class="font-semibold text-slate-700">{{ format(item.projected) }}</span>
                    </div>
                </div>
                <div class="text-right">
                    <div class="inline-flex items-center gap-1 text-xs font-semibold"
                        [class.text-emerald-600]="item.deltaPct > 0" [class.text-rose-600]="item.deltaPct < 0"
                        [class.text-slate-500]="item.deltaPct === 0">
                        <i class="pi" [class.pi-sort-up-fill]="item.deltaPct > 0"
                            [class.pi-sort-down-fill]="item.deltaPct < 0" [class.pi-minus]="item.deltaPct === 0"></i>
                        <span>{{ item.deltaPct }}%</span>
                    </div>
                    <div class="text-[11px] font-semibold" [class.text-emerald-600]="item.deltaAbs > 0"
                        [class.text-rose-600]="item.deltaAbs < 0" [class.text-slate-500]="item.deltaAbs === 0">
                        {{ format(item.deltaAbs) }}
                    </div>
                </div>
            </div>
            }
        </div>
        <div class="px-4 py-3 lg:px-6 bg-surface-50 text-[11px] text-slate-500">
            <span class="font-semibold text-slate-700">Note:</span>
            {{ cvdNote }}
        </div>
        }

        <!-- CVD list view -->
    </div>
</div>